import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleHeart, X, Send, Phone, Download } from "lucide-react";
import { API } from "../lib/api";
import { renderWalletCard, downloadHelplineCard } from "../lib/helplineCard";

const GREETING = { role: "assistant", content: "Kia ora, I'm Aroha — the movement's support companion. Whether it's you or someone you love, no judgement here. What's on your mind?" };
const CHIPS = ["I think I need help", "How do I help someone I love?", "What does meth do to the brain?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [crisis, setCrisis] = useState(false);
  const [cardOffered, setCardOffered] = useState(false);
  const [cardDismissed, setCardDismissed] = useState(false);
  const repliesRef = useRef(0);
  const sidRef = useRef(localStorage.getItem("aroha_sid") || (() => { const id = crypto.randomUUID(); localStorage.setItem("aroha_sid", id); return id; })());
  const bottomRef = useRef(null);

  const scroll = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    scroll();
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(part.slice(5));
            if (evt.type === "meta" && evt.crisis) { setCrisis(true); scroll(); }
            if (evt.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.content };
                return copy;
              });
              scroll();
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble connecting right now. If you need support, free call or text 1737 anytime, or the Alcohol & Drug Helpline 0800 787 797." };
        return copy;
      });
    } finally {
      setBusy(false);
      repliesRef.current += 1;
      if (repliesRef.current === 3) setCardOffered(true);
      scroll();
    }
  };

  return (
    <>
      <motion.button
        data-testid="ai-chat-trigger-button"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.6, type: "spring" }}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#1E4B3E] hover:bg-[#16382e] text-[#F5F2EC] shadow-2xl flex items-center justify-center transition-colors"
        aria-label="Talk to Aroha, the support companion"
      >
        {open ? <X size={22} /> : <MessageCircleHeart size={24} />}
        {!open && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#F59E0B] rounded-full animate-pulse" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm h-[32rem] bg-[#0B0C10] border border-[#212636] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-[#212636] flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#1E4B3E] flex items-center justify-center"><MessageCircleHeart size={16} className="text-[#F59E0B]" /></div>
              <div>
                <p className="font-display font-bold text-white text-sm">Aroha</p>
                <p className="text-[10px] text-slate-500">AI companion · not monitored 24/7 · not a crisis line</p>
              </div>
            </div>

            {crisis && (
              <div className="bg-[#B91C1C] text-white px-4 py-2.5 text-xs" data-testid="chat-crisis-banner">
                <div className="flex items-center gap-2">
                  <Phone size={13} className="shrink-0" />
                  <span>You matter. If you're in danger call <a href="tel:111" className="font-bold underline">111</a> · free call/text <a href="tel:1737" className="font-bold underline">1737</a> (24/7) · text <a href="sms:8681" className="font-bold underline">8681</a></span>
                </div>
                <div className="mt-1.5 pl-[21px] text-[11px] text-red-100 leading-relaxed" data-testid="chat-crisis-meth-lines">
                  Meth support: <a href="tel:0800787797" className="font-bold underline">0800 787 797</a> · Māori <a href="tel:0800787798" className="font-bold underline">0800 787 798</a> · Pasifika <a href="tel:0800787799" className="font-bold underline">0800 787 799</a> · Youth <a href="tel:0800787984" className="font-bold underline">0800 787 984</a>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#EF4444] text-white" : "bg-[#12141C] text-slate-200 border border-[#212636]"}`}>
                    {m.content || (busy && i === messages.length - 1 ? <span className="animate-pulse">…</span> : "")}
                  </div>
                </div>
              ))}
              {cardOffered && !cardDismissed && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="chat-card-offer"
                  className="border border-[#1E4B3E] bg-[#0F1512] rounded-2xl p-3.5">
                  <p className="text-xs text-slate-300 leading-relaxed mb-2.5">Want to keep these numbers close? Here's a wallet card with every helpline — yours to keep, or to pass on to someone who needs it.</p>
                  <div className="flex items-center gap-2">
                    <button data-testid="chat-card-download-button" onClick={async () => downloadHelplineCard(await renderWalletCard(), "break-the-cycle-helpline-wallet-card")}
                      className="inline-flex items-center gap-1.5 bg-[#EF4444] hover:bg-[#DC2626] text-white text-[11px] font-bold rounded-full px-3 py-1.5 transition-colors">
                      <Download size={12} /> Download wallet card
                    </button>
                    <button data-testid="chat-card-dismiss-button" onClick={() => setCardDismissed(true)}
                      className="text-[11px] text-slate-500 hover:text-slate-300 px-2 transition-colors">No thanks</button>
                  </div>
                </motion.div>
              )}
              <div ref={bottomRef} />
            </div>

            {messages.length === 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {CHIPS.map((c) => (
                  <button key={c} data-testid={`chat-chip-${CHIPS.indexOf(c)}`} onClick={() => send(c)} className="text-[11px] border border-[#212636] text-slate-400 hover:text-white hover:border-slate-500 rounded-full px-3 py-1 transition-colors">
                    {c}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#212636] flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Kōrero with me…" maxLength={2000}
                data-testid="ai-chat-message-input"
                className="flex-1 bg-[#12141C] border border-[#212636] rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#1E4B3E]" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="ai-chat-send-button"
                className="w-10 h-10 rounded-full bg-[#1E4B3E] hover:bg-[#16382e] disabled:opacity-40 text-white flex items-center justify-center transition-colors" aria-label="Send">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
